/**
 * Activity Log API Contract
 *
 * ts-rest contract definition for fetching a job's activity log.
 *
 * Endpoint  : GET /jobs/activity-log/:jobId
 * Method    : GET
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Paginated list of activity log entries
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single activity log entry
 */
export const activityLogItemSchema = z.object({
  id: z.number(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  activity: z.string().nullable(),
  status: z.string().nullable(),
  created_time: z.string().nullable(),
  created_by: z.string().nullable(),
});

/**
 * Zod schema for the paginated activity log response
 */
export const activityLogResponseSchema = z.object({
  data: z.array(activityLogItemSchema),
  total: z.number(),
  limit: z.number(),
  skip: z.number(),
});

/**
 * Zod schema for query parameters
 */
export const activityLogQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
});

/**
 * TypeScript types inferred from Zod schemas
 */
export type ActivityLogItem = z.infer<typeof activityLogItemSchema>;
export type ActivityLogResponse = z.infer<typeof activityLogResponseSchema>;
export type ActivityLogQuery = z.infer<typeof activityLogQuerySchema>;

/**
 * Activity Log API Contract
 *
 * Base path: /jobs/activity-log  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts)
 */
export const activityLogContract = c.router({
  /**
   * Get activity log entries for a job
   *
   * GET /jobs/activity-log/:jobId?limit=50&skip=0
   *
   * Returns paginated activity log entries ordered by created_time DESC.
   */
  getActivityLog: {
    method: 'GET',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    query: activityLogQuerySchema,
    responses: {
      200: activityLogResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get activity log entries for a job',
    description:
      'Returns paginated activity log entries for a specific job, ordered by most recent first.',
  },
});
